import config from '../../../config';
import {checkStatus, authFetch, parseHeadersAndJson} from '../../utils/http';
import {getOrigin} from '../../utils/path';

const USER_API = `${config.userApi}/api/v1/users`;

export default class LiveUserService {
  searchByOrganisation(organisationId, query = '', page = 0, size = 10) {
    return authFetch(`${USER_API}/search?organisationId=${organisationId}&page=${page}&size=${size}&query=${query}`)
      .then(checkStatus)
      .then(parseHeadersAndJson)
      .then(({json, headers}) => ({
        users: json.map(user => ({
          ...user,
          organisationRole: user.roleByOrganisationIds[organisationId]
        })),
        totalCount: Number(headers.get('X-Total-Count'))
      }));
  }

  publicSearchByOrganisation(organisationId, query = '', page = 0, size = 10) {
    return authFetch(`${USER_API}/public/search?organisationId=${organisationId}&page=${page}&size=${size}&query=${query}`)
      .then(checkStatus)
      .then(parseHeadersAndJson)
      .then(({json, headers}) => ({
        users: json.map(user => ({
          ...user,
          organisationRole: user.roleByOrganisationIds[organisationId]
        })),
        totalCount: Number(headers.get('X-Total-Count'))
      }));
  }

  searchByEmail(email) {
    return authFetch(`${USER_API}?email=${email}`)
      .then(checkStatus);
  }

  getUserDetailsById(userId) {
    return authFetch(`${USER_API}/${userId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  createUser(user) {
    const data = {
      ...user,
      client: config.keycloak.clientId,
      redirectUri: getOrigin()
    };

    return fetch(`${USER_API}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus)
      .then(response => response.json());
  }

  updateUserDetails(userDetails) {
    return authFetch(`${USER_API}/${userDetails.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userDetails)
    }).then(checkStatus);
  }

  updateOptions(userId, options) {
    return authFetch(`${USER_API}/${userId}/options`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({options})
    }).then(checkStatus);
  }

  linkOrganisationOnRegister(organisationId, userId) {
    return authFetch(`${USER_API}/link/${userId}/${organisationId}`, {
      method: 'POST'
    })
      .then(checkStatus)
      .then(response => response.json());
  }

  linkOrganisationToUsers(organisationId, users) {
    return authFetch(`${USER_API}/link/users/${organisationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(users)
    })
      .then(checkStatus)
      .then(response => response.json());
  }

  linkOrganisationToNewUsers(organisationId, users) {
    return authFetch(`${USER_API}/link/users/${organisationId}/create-by-third`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(users)
    })
      .then(checkStatus)
      .then(response => response.json());
  }

  unlinkUserFromOrganisation(userId, organisationId) {
    return authFetch(`${USER_API}/unlink/${userId}/${organisationId}`, {
      method: 'POST'
    })
      .then(checkStatus);
  }

  sendActivationEmail(userId) {
    const data = {
      client: config.keycloak.clientId,
      redirectUri: getOrigin()
    };

    return fetch(`${USER_API}/${userId}/activation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus);
  }

  updateLanguage(userId, lang) {
    const data = {
      language: lang
    };
    return authFetch(`${USER_API}/${userId}/language`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus);
  }

  requestUpdateEmail(userId, email) {
    const data = {
      email
    };
    return authFetch(`${USER_API}/${userId}/email`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(checkStatus);
  }

  updateEmail(token) {
    const data = {
      token
    };
    return fetch(`${USER_API}/email`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(checkStatus);
  }

  getGeneralTermsLastMajorVersionUpdateTimestamp() {
    return authFetch(`${USER_API}/general-terms`)
      .then(checkStatus)
      .then(response => response.json())
      .then(json => json.lastMajorVersionUpdateTimestamp);
  }

  getPrivacyPolicyLastMajorVersionUpdateTimestamp() {
    return authFetch(`${USER_API}/privacy-policy`)
      .then(checkStatus)
      .then(response => response.json())
      .then(json => json.lastMajorVersionUpdateTimestamp);
  }

  getAcceptationDomainNameAutomaticTieUser(organisationId) {
    return authFetch(`${USER_API}/domain-name?organisationId=${organisationId}`)
      .then(checkStatus)
      .then(response => response.json());
  }

  createInvitationByUser(token) {
    const data = {token};
    return fetch(`${USER_API}/invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(checkStatus)
      .then(response => response.status === 200)
      .catch(error => {
        if (error.response && error.response.status === 404) {
          return false;
        }

        throw error;
      });
  }

  acceptLegalConditions(legalConditions) {
    return authFetch(`${USER_API}/legal-conditions`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(legalConditions)
    }).then(checkStatus);
  }

  doesUserExistsInArtio() {
    return authFetch(`${USER_API}/artio-user-exists`)
      .then(response => response.json())
      .then(response => response.result)
      .catch(() => false);
  }
}
